import React from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag, Plane, Briefcase, ShoppingCart, ArrowRight,
  Zap, BadgeDollarSign, CreditCard, FileCheck, Coins, Plug, Headphones, Landmark,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { useReveal } from "../../hooks/useReveal";
import { ASSETS, CLIENT_LOGOS, PAYMENT_METHODS } from "../../mock";
import PaymentBadge from "./PaymentBadges";

const chipIcons = [ShoppingBag, Plane, Briefcase, ShoppingCart];

// 3D tilt effect for the hero card (rests at a slight elegant angle)
const useTilt = (maxDeg = 12, baseY = -6, baseX = 4) => {
  const ref = React.useRef(null);

  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `perspective(1000px) rotateY(${baseY + px * maxDeg}deg) rotateX(${baseX - py * maxDeg}deg) scale(1.02)`;
  };

  const onMouseLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = `perspective(1000px) rotateY(${baseY}deg) rotateX(${baseX}deg) scale(1)`;
  };

  return { ref, onMouseMove, onMouseLeave };
};

export const Hero = () => {
  const { t } = useLanguage();
  const tilt = useTilt(14);

  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="inicio" className="relative pt-[72px] overflow-hidden bg-white">
      {/* decorative gradient blob */}
      <div className="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#0fff9b]/30 to-[#0fe814]/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-10 lg:pb-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div className="relative z-10">
            {/* industry chips */}
            <div className="flex flex-wrap gap-2 mb-7">
              {t.hero.chips.map((chip, i) => {
                const Icon = chipIcons[i];
                return (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-black/70 bg-black/[0.04] border border-black/[0.07] px-3.5 py-1.5 rounded-full"
                  >
                    <Icon size={13} className="text-[#0fb013]" />
                    {chip}
                  </span>
                );
              })}
            </div>

            <h1 className="font-extrabold text-[38px] sm:text-[48px] lg:text-[56px] leading-[1.05] tracking-tight text-black">
              {t.hero.title1}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{t.hero.title2}</span>
                <span className="absolute bottom-1 left-0 w-full h-[14px] bg-[#0fe814]/60 -z-0 rounded-sm" />
              </span>
            </h1>

            <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-black/60 max-w-xl font-medium">
              {t.hero.subtitle}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/registro"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 text-[14px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-8 py-4 rounded-full transition-all duration-200 shadow-[0_6px_20px_rgba(15,232,20,0.4)] hover:shadow-[0_8px_28px_rgba(15,176,19,0.45)] hover:-translate-y-0.5"
              >
                {t.hero.ctaPrimary}
                <ArrowRight size={17} />
              </Link>
              <a
                href="#contacto"
                onClick={(e) => scrollTo(e, "#contacto")}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center text-[14px] font-bold text-black px-8 py-4 rounded-full border-2 border-black/15 hover:border-black transition-colors duration-200"
              >
                {t.hero.ctaSecondary}
              </a>
            </div>

            {/* payment methods mini strip */}
            <div className="mt-10 flex flex-wrap items-center gap-2">
              {PAYMENT_METHODS.map((m) => (
                <PaymentBadge key={m.name} style={m.style} size="md" />
              ))}
            </div>
          </div>

          {/* Card visual with 3D tilt */}
          <div className="relative flex justify-center lg:justify-end lg:pr-6">
            <div className="relative w-full max-w-[300px] sm:max-w-[360px] lg:max-w-[400px] hero-card-float" style={{ perspective: "1000px" }}>
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0fe814]/15 via-transparent to-[#0fff9b]/20 rounded-[40px] blur-2xl scale-90" />
              <div
                ref={tilt.ref}
                onMouseMove={tilt.onMouseMove}
                onMouseLeave={tilt.onMouseLeave}
                data-testid="hero-card-tilt"
                className="relative hero-card-tilt"
                style={{ transform: "perspective(1000px) rotateY(-6deg) rotateX(4deg)" }}
              >
                <img
                  src={ASSETS.card}
                  alt="Tarjeta RapidPay"
                  className="w-full h-auto rounded-[22px] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.25)] pointer-events-none select-none"
                  draggable="false"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const LogoStrip = () => {
  const { t } = useLanguage();
  const logos = [...CLIENT_LOGOS, ...CLIENT_LOGOS];

  return (
    <section className="py-10 bg-white border-y border-black/[0.06] overflow-hidden">
      <p className="text-center text-[12px] font-semibold uppercase tracking-[0.18em] text-black/40 mb-7">
        {t.hero.trusted}
      </p>
      <div className="relative">
        <div className="logo-marquee flex items-center gap-14 whitespace-nowrap">
          {logos.map((name, i) => (
            <span
              key={`${name}-${i}`}
              className="text-[19px] font-extrabold tracking-tight text-black/30 hover:text-black/60 transition-colors duration-300 select-none"
            >
              {name}
            </span>
          ))}
        </div>
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent pointer-events-none" />
      </div>
    </section>
  );
};

const benefitIcons = [Zap, BadgeDollarSign, CreditCard, FileCheck, Coins, Plug, Headphones, Landmark];

export const Benefits = () => {
  const { t } = useLanguage();
  const [ref, visible] = useReveal();

  return (
    <section id="beneficios" className="py-20 lg:py-28 bg-[#fafcf9]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <h2 className="font-extrabold text-[28px] sm:text-[36px] lg:text-[42px] leading-tight tracking-tight text-black max-w-3xl">
          {t.benefits.title}
        </h2>

        <div
          ref={ref}
          className={`mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {t.benefits.items.map((item, i) => {
            const Icon = benefitIcons[i];
            return (
              <div
                key={i}
                data-testid={`benefit-card-${i}`}
                className="group bg-white border border-black/[0.06] rounded-2xl p-6 hover:border-[#0fe814]/50 hover:shadow-[0_12px_32px_rgba(15,176,19,0.10)] transition-all duration-300 hover:-translate-y-1"
                style={{ transitionDelay: visible ? `${i * 40}ms` : "0ms" }}
              >
                <div className="w-11 h-11 rounded-xl bg-[#0fe814]/15 flex items-center justify-center mb-5 group-hover:bg-[#0fe814]/30 transition-colors duration-300">
                  <Icon size={21} className="text-[#0fb013]" />
                </div>
                <h3 className="font-bold text-[16px] text-black leading-snug">{item.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-black/55 font-medium">{item.desc}</p>
                {item.showMethods && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {PAYMENT_METHODS.slice(0, 6).map((m) => (
                      <PaymentBadge key={m.name} style={m.style} size="sm" />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
